import {
  ComponentType,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { StateCreator } from 'zustand';
import { createWithEqualityFn } from 'zustand/traditional';

import { TimeStoreAction, TimeStoreState } from '../../models/TimeStore';

type Store = TimeStoreState & TimeStoreAction;

const initializer: StateCreator<Store, [], []> = (set, get) => {
  // clock
  const initClock: TimeStoreAction['initClock'] = () => {
    if (get().now !== undefined) {
      throw new Error('clock should only be initialized once');
    }
    set({ now: Date.now(), timers: {} });
  };

  const tick: TimeStoreAction['tick'] = () => {
    set({ now: Date.now() });
  };

  const resetClock: TimeStoreAction['resetClock'] = () => {
    set({ now: undefined, timers: {} });
  };

  // timer
  const registerTimer: TimeStoreAction['registerTimer'] = (name, timeLimit) => {
    if (get().timers[name]) {
      throw new Error('timer already exists');
    }
    set((state) => ({
      timers: {
        ...state.timers,
        [name]: {
          lastSplitTimeElapsed: 0,
          lastTimeLimit: timeLimit,
          lastTimeElapsed: 0,
          timeLimit,
          timeElapsed: 0,
          startTimestamp: undefined,
        },
      },
    }));
  };

  const unregisterTimer: TimeStoreAction['unregisterTimer'] = (name) => {
    if (!get().timers[name]) {
      throw new Error('timer not exists');
    }
    set((state) => {
      // ignore rest siblings?
      /*eslint @typescript-eslint/no-unused-vars: ["error", { "ignoreRestSiblings": true }]*/
      const { [name]: timer, ...rest } = state.timers;
      return { timers: rest };
    });
  };

  const startTimer: TimeStoreAction['startTimer'] = (name) => {
    set((state) => {
      const now = state.now;
      const { [name]: timer, ...rest } = state.timers;
      if (now && timer) {
        return {
          timers: {
            ...rest,
            [name]: {
              ...timer,
              startTimestamp: now,
            },
          },
        };
      }
      return state;
    });
  };

  const pauseTimer: TimeStoreAction['pauseTimer'] = (name) => {
    set((state) => {
      const now = state.now;
      const { [name]: timer, ...rest } = state.timers;
      if (now && timer?.startTimestamp) {
        return {
          timers: {
            ...rest,
            [name]: {
              ...timer,
              timeElapsed: timer.timeElapsed + now - timer.startTimestamp,
              startTimestamp: undefined,
            },
          },
        };
      }
      return state;
    });
  };

  const lapTimer: TimeStoreAction['lapTimer'] = (name, timeLimit) => {
    set((state) => {
      const now = state.now;
      const { [name]: timer, ...rest } = state.timers;
      if (now && timer) {
        const lastTimeElapsed =
          timer.timeElapsed + now - (timer.startTimestamp ?? now);
        return {
          timers: {
            ...rest,
            [name]: {
              lastSplitTimeElapsed:
                timer.lastSplitTimeElapsed + lastTimeElapsed,
              lastTimeLimit: timer.timeLimit,
              lastTimeElapsed,
              timeLimit: timeLimit ?? timer.timeLimit,
              timeElapsed: 0,
              startTimestamp: undefined,
            },
          },
        };
      }
      return state;
    });
  };

  const resetTimer: TimeStoreAction['resetTimer'] = (name, timeLimit) => {
    set((state) => {
      const now = state.now;
      const { [name]: timer, ...rest } = state.timers;
      if (now && timer) {
        return {
          timers: {
            ...rest,
            [name]: {
              lastSplitTimeElapsed: 0,
              lastTimeLimit: timeLimit ?? timer.timeLimit,
              lastTimeElapsed: 0,
              timeLimit: timeLimit ?? timer.timeLimit,
              timeElapsed: 0,
              startTimestamp: undefined,
            },
          },
        };
      }
      return state;
    });
  };

  const getTimer: TimeStoreAction['getTimer'] = (name) => {
    let overallTimeElapsed = 0;
    let lastTimeLimit;
    let lastTimeElapsed = 0;
    let timeLimit;
    let timeElapsed = 0;
    let isRunning = false;

    const {
      now,
      timers: { [name]: timer },
    } = get();
    if (now && timer) {
      lastTimeLimit = timer.lastTimeLimit;
      lastTimeElapsed = timer.lastTimeElapsed;
      timeLimit = timer.timeLimit;
      timeElapsed = timer.timeElapsed + now - (timer.startTimestamp ?? now);
      isRunning = timer.startTimestamp !== undefined;
      overallTimeElapsed = timer.lastSplitTimeElapsed + timeElapsed;
    }

    return {
      overallTimeElapsed,
      lastTimeLimit,
      lastTimeElapsed,
      timeLimit,
      timeElapsed,
      isRunning,
      isTimeout: timeElapsed >= (timeLimit ?? Infinity),
    };
  };

  return {
    now: undefined,
    timers: {},
    initClock,
    tick,
    resetClock,
    registerTimer,
    unregisterTimer,
    startTimer,
    pauseTimer,
    lapTimer,
    resetTimer,
    getTimer,
  };
};

export const useTimeStore = createWithEqualityFn<Store>()(
  initializer,
  Object.is,
);

export const useClock = () => {
  const { initClock, tick, resetClock } = useTimeStore(
    (state) => ({
      initClock: state.initClock,
      tick: state.tick,
      resetClock: state.resetClock,
    }),
    () => true,
  );

  useEffect(() => {
    initClock();
    let id: number;
    const step = () => {
      tick();
      id = requestAnimationFrame(step);
    };
    step();
    return () => {
      cancelAnimationFrame(id);
      resetClock();
    };
  }, [initClock, tick, resetClock]);
};

export const Clock = () => {
  // cannot put useClock in the root component <App /> because the children component will render first
  // so wrap it whit a component and put at the first component to have it render first
  useClock();
  return <></>;
};

export const useNow = (interval = 1000, shouldCorrect = true) => {
  let now = useTimeStore(
    (state) => state.now ?? 0,
    (oldValue, newValue) =>
      Math.floor(oldValue / interval) === Math.floor(newValue / interval),
  );
  if (shouldCorrect) {
    now = Math.floor(now / interval) * interval;
  }
  return now;
};

export const withNow = <P extends { now: number }>(
  Component: ComponentType<P>,
  interval = 1000,
  shouldCorrect = true,
) => {
  return forwardRef<unknown, Omit<P, 'now'>>((props, ref) => {
    const now = useNow(interval, shouldCorrect);
    return <Component ref={ref} {...(props as unknown as P)} now={now} />;
  });
};

export const useCountdown = (
  till: number,
  interval = 1000,
  shouldCorrect = true,
) => {
  let countdown = useTimeStore(
    (state) => Math.max(till - (state.now ?? 0), 0),
    (oldValue, newValue) =>
      Math.ceil(oldValue / interval) === Math.ceil(newValue / interval),
  );
  if (shouldCorrect) {
    countdown = Math.ceil(countdown / interval) * interval;
  }
  return countdown;
};

export const withCountdown = <
  P extends { till: number },
  R = { countdown: number },
>(
  Component: ComponentType<Omit<P, keyof R> & R>,
  addParams: (countdown: number, params: P) => R,
  interval = 1000,
  shouldCorrect = true,
) => {
  return forwardRef<unknown, P>((p, ref) => {
    const { till, ...props } = p;
    const countdown = useCountdown(till, interval, shouldCorrect);
    const params = useMemo(() => addParams(countdown, p), [countdown, p]);
    return (
      <Component
        ref={ref}
        {...(props as unknown as P)}
        {...(params as unknown as R)}
      />
    );
  });
};

export const useTimer = (name: string, timeLimit?: number) => {
  const {
    registerTimer,
    unregisterTimer,
    startTimer,
    pauseTimer,
    lapTimer,
    resetTimer,
  } = useTimeStore(
    (state) => ({
      registerTimer: state.registerTimer,
      unregisterTimer: state.unregisterTimer,
      startTimer: state.startTimer,
      pauseTimer: state.pauseTimer,
      lapTimer: state.lapTimer,
      resetTimer: state.resetTimer,
    }),
    () => true,
  );

  useEffect(() => {
    registerTimer(name, timeLimit);
    return () => unregisterTimer(name);
  }, [name, timeLimit, registerTimer, unregisterTimer]);

  const start = useCallback(() => startTimer(name), [name, startTimer]);
  const pause = useCallback(() => pauseTimer(name), [name, pauseTimer]);
  const newLap = useCallback(
    (newTimeLimit?: number) => lapTimer(name, newTimeLimit),
    [name, lapTimer],
  );
  const reset = useCallback(
    (newTimeLimit?: number) => resetTimer(name, newTimeLimit),
    [name, resetTimer],
  );

  return useMemo(
    () => ({ start, pause, newLap, reset }),
    [start, pause, newLap, reset],
  );
};

export const useTimerState = (name: string, interval = 1000) => {
  return useTimeStore(
    (state) => state.getTimer(name),
    (oldTimer, newTimer) =>
      oldTimer.timeLimit === newTimer.timeLimit &&
      oldTimer.isRunning === newTimer.isRunning &&
      oldTimer.isTimeout === newTimer.isTimeout &&
      Math.floor(oldTimer.timeElapsed / interval) ===
        Math.floor(newTimer.timeElapsed / interval),
  );
};
